import Header from "./header/header";
import NavBar from "./nav/nav-bar";
import Footer from "./footer/footer";
import { useHistory, useParams } from "react-router";
import { useEffect, useState, useMemo } from "react";
import Service from "./webservice/http";
import ClipLoader from "react-spinners/ClipLoader";
import { BiError } from "react-icons/bi";
import { RiSignalWifiErrorFill } from "react-icons/ri";
import { storage } from "./utils/storage";

const PERIODS = [
  { key: "0-30", label: "< 30 Days", min: 0, max: 30 },
  { key: "30-60", label: "> 30 Days", min: 30, max: 60 },
  { key: "60-90", label: "< 60 Days", min: 60, max: 90 },
  { key: "90+", label: "< 90 Days", min: 90, max: Infinity },
];

const INTENT_ASPECTS = [
  { key: "willingness", label: "Willingness to Pay", color: "#270086" },
  { key: "capability", label: "Financial Capability", color: "#1A0059" },
  {
    key: "responsiveness",
    label: "Communication Responsiveness",
    color: "#00bd35",
  },
  { key: "commitment", label: "Commitment Level", color: "#F99E52" },
  { key: "riskOfDefault", label: "Risk of Default", color: "#ca5356" },
];

const MOCK_INTENT = {
  "0-30": {
    willingness: 72,
    capability: 65,
    responsiveness: 80,
    commitment: 58,
    riskOfDefault: 35,
  },
  "30-60": {
    willingness: 60,
    capability: 55,
    responsiveness: 65,
    commitment: 45,
    riskOfDefault: 50,
  },
  "60-90": {
    willingness: 45,
    capability: 40,
    responsiveness: 50,
    commitment: 35,
    riskOfDefault: 65,
  },
  "90+": {
    willingness: 30,
    capability: 30,
    responsiveness: 35,
    commitment: 25,
    riskOfDefault: 80,
  },
};

const MOCK_RECOMMENDATIONS = {
  "0-30":
    "Customer shows moderate willingness. Continue regular follow-ups via phone. Recommend offering flexible payment plan options to increase commitment.",
  "30-60":
    "Responsiveness declining. Escalate to senior agent. Consider sending formal reminder via email with updated payment schedule and consequences of non-payment.",
  "60-90":
    "High risk of default detected. Recommend final notice with legal warning. Explore settlement options or partial payment agreements.",
  "90+":
    "Account requires immediate escalation review. Evaluate for legal proceedings or transfer to third-party DCA based on recovery probability assessment.",
};

/* ─── Period Trail Data: shown in the moving info card below calendars ─── */
const PERIOD_TRAIL_DATA = {
  "0-30": {
    consumableInsight:
      "Customer engaged positively on first contact. Initial payment promise secured within the first 30 days.",
    nextAction:
      "Schedule a follow-up call within 7 days to confirm payment commitment and amount.",
    missingDataPoint:
      "Exact promised payment amount and preferred payment mode not yet captured.",
  },
  "30-60": {
    consumableInsight:
      "Responsiveness declining. Two scheduled follow-ups missed. Tone shift detected in last call.",
    nextAction:
      "Escalate to senior agent. Send formal email reminder with updated due date and payment schedule.",
    missingDataPoint:
      "Updated contact number and alternate payer or guarantor details missing.",
  },
  "60-90": {
    consumableInsight:
      "High default risk flagged. Customer unresponsive to last 3 outreach attempts across channels.",
    nextAction:
      "Issue final notice. Explore partial payment or settlement agreement options immediately.",
    missingDataPoint:
      "Current employment status, income proof, and alternate address not captured.",
  },
  "90+": {
    consumableInsight:
      "Account severely overdue. Recovery probability assessed as critically low by AI model.",
    nextAction:
      "Initiate legal review or transfer to third-party Debt Collection Agency (DCA).",
    missingDataPoint:
      "Asset declarations, legal contact address, and co-borrower details are absent.",
  },
};

/** Safely parse call_date strings which may be DD-MM-YYYY or YYYY-MM-DD (ISO). */
const parseDate = (s) => {
  if (!s) return new Date(NaN);
  // ISO format: YYYY-MM-DD… — Date constructor handles this correctly
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s);
  // DD-MM-YYYY or DD/MM/YYYY — re-order to YYYY-MM-DD
  const parts = s.split(/[-\/]/);
  if (parts.length === 3 && parts[2].length === 4) {
    return new Date(
      `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`,
    );
  }
  return new Date(s);
};

/* ─── Inline style objects ─── */
const styles = {
  infoBar: {
    display: "flex",
    gap: "40px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  infoLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  infoValue: { fontSize: "14px", fontWeight: 500, color: "#1A0059" },
  badge: (color) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 20px",
    borderRadius: "20px",
    backgroundColor: color + "15",
    border: `1px solid ${color}`,
    color,
    fontSize: "13px",
    fontWeight: 600,
  }),
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#888",
    fontSize: "14px",
  },
  thTd: { padding: "10px 16px" },
  recBox: {
    backgroundColor: "#f8f6ff",
    border: "1px solid #e8e3fa",
    borderLeft: "4px solid #270086",
    borderRadius: "6px",
    padding: "16px 20px",
  },
  recLabel: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#270086",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "8px",
  },
  sendBtn: {
    padding: "8px 32px",
    borderRadius: "6px",
    backgroundColor: "#270086",
    color: "#fff",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
  },
  barTrack: {
    height: "8px",
    backgroundColor: "#eef2f5",
    borderRadius: "4px",
    overflow: "hidden",
    flex: 1,
  },
  resCard: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "14px 20px",
    borderRadius: "8px",
  },
  resIcon: (color) => ({
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: color + "20",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    flexShrink: 0,
  }),
  scoreGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "12px",
  },
  scoreCard: {
    textAlign: "center",
    padding: "14px 8px",
    backgroundColor: "#f8f6ff",
    borderRadius: "8px",
    border: "1px solid #e8e3fa",
  },
};

const PromiseDetails = () => {
  const { leadID } = useParams();
  const history = useHistory();
  const services = new Service();

  const [leadDetail, setLeadDetail] = useState(null);
  const [isPending, setIsPending] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("0-30");
  const [activetab, setActivetab] = useState("calls");
  const [newtask, setNewtask] = useState("");
  const [edit, setEdit] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState(null);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState(null);

  const [callInsight, setCallInsight] = useState(null);

  function fetchData() {
    services.get(`api/call/promise_to_pay/${leadID}/`).then((res) => {
      setIsPending(false);
      if (res == "TypeError: Failed to fetch") {
        setError("Connection Error");
      } else {
        try {
          if (res.code == "token_not_valid") {
            storage.clear();
            history.push("/login");
          }
          setError(null);
          setLeadDetail(res);
        } catch (e) {
          setError(e);
        }
      }
    });

    services.get(`api/call/${leadID}/call_insight/`).then((res) => {
      if (
        res !== "TypeError: Failed to fetch" &&
        res.code !== "token_not_valid"
      ) {
        setCallInsight(res);
      }
    });
  }

  useEffect(() => {
    setLeadDetail(null);
    setIsPending(true);
    setError(null);
    fetchData();
  }, []);

  const addNotes = async () => {
    if (!newtask.trim()) return;
    setNoteLoading(true);
    setNoteError(null);
    try {
      const res = await services.post(`api/call/promise_to_pay/${leadID}/`, {
        note: newtask,
      });
      if (res === "TypeError: Failed to fetch") {
        setNoteError("Failed to post note. Please try again.");
      } else {
        setNewtask("");
        fetchData();
      }
    } catch (err) {
      setNoteError("Failed to post note. Please try again.");
    } finally {
      setNoteLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    setDeletingNoteId(noteId);
    setNoteError(null);
    try {
      const res = await services.delete(
        `api/call/promise_to_pay/${leadID}/notes/${noteId}/`
      );
      if (res === "TypeError: Failed to fetch") {
        setNoteError("Failed to delete note. Please try again.");
      } else {
        fetchData();
      }
    } catch (err) {
      setNoteError("Failed to delete note. Please try again.");
    } finally {
      setDeletingNoteId(null);
    }
  };

  const groupedCalls = useMemo(() => {
    if (!leadDetail?.calls?.length) return {};
    const dates = leadDetail.calls
      .map((c) => parseDate(c.call_date))
      .filter((d) => !isNaN(d));
    if (!dates.length) return {};
    const ref = new Date(Math.min(...dates));
    const groups = {};
    PERIODS.forEach((p) => (groups[p.key] = []));
    leadDetail.calls.forEach((call) => {
      const d = parseDate(call.call_date);
      if (isNaN(d)) return;
      const diff = Math.floor((d - ref) / 864e5);
      for (const p of PERIODS) {
        if (diff >= p.min && (diff < p.max || p.max === Infinity)) {
          groups[p.key].push(call);
          break;
        }
      }
    });
    return groups;
  }, [leadDetail]);

  const intentScores = leadDetail?.intent_scores || MOCK_INTENT;
  const recommendations = leadDetail?.recommendations || MOCK_RECOMMENDATIONS;
  const currentCalls = groupedCalls[selectedPeriod] || [];
  const currentIntent = intentScores[selectedPeriod] || {};
  const currentRec = recommendations[selectedPeriod] || "";

  const resolution = useMemo(() => {
    if (!leadDetail) return null;
    const s = leadDetail.resolution_status || leadDetail.status;
    if (s === "Collected" || s === "Converted")
      return { label: "Collection Made", icon: "✅", color: "#00bd35" };
    if (s === "Legal Action")
      return { label: "Legal Action", icon: "⚖️", color: "#ca5356" };
    if (s === "Transferred to DCA")
      return { label: "Transferred to DCA", icon: "🔄", color: "#F99E52" };
    return {
      label: "In Progress",
      icon: "⏳",
      color: "#270086",
      ongoing: true,
    };
  }, [leadDetail]);

  const customerInfo = useMemo(() => {
    if (!leadDetail?.calls?.length) return {};
    const c = leadDetail.calls[0];
    return {
      customer: c?.customer || "-",
      product: c?.product || "-",
      agent: c?.agent || "-",
      category: leadDetail?.category || "-",
    };
  }, [leadDetail]);

  const overallIntent = useMemo(() => {
    const keys = INTENT_ASPECTS.map((a) => a.key);
    const avg = {};
    keys.forEach((k) => {
      const vals = Object.values(intentScores).map((s) => s[k] || 0);
      avg[k] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    });
    return avg;
  }, [intentScores]);

  const IntentBar = ({ aspect, value }) => (
    <div style={{ marginBottom: "14px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "4px",
        }}
      >
        <span style={{ fontSize: "13px", color: "#333", fontWeight: 500 }}>
          {aspect.label}
        </span>
        <span
          style={{ fontSize: "13px", fontWeight: 700, color: aspect.color }}
        >
          {value}%
        </span>
      </div>
      <div style={styles.barTrack}>
        <div
          style={{
            width: `${value}%`,
            height: "100%",
            backgroundColor: aspect.color,
            borderRadius: "4px",
            transition: "width 0.4s ease",
          }}
        />
      </div>
    </div>
  );

  const MiniCalendar = ({
    year,
    month,
    callDates,
    dueDates,
    disbursedDates,
    isActive,
    isClickable,
    onClick,
    label,
  }) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const today = new Date();
    const isCurrentMonth =
      today.getFullYear() === year && today.getMonth() === month;
    const isToday = (day) => isCurrentMonth && today.getDate() === day;
    const isCallDay = (day) =>
      callDates.some((cd) => {
        const d = new Date(cd);
        return (
          d.getFullYear() === year &&
          d.getMonth() === month &&
          d.getDate() === day
        );
      });
    const isDueDay = (day) =>
      dueDates.some((dd) => {
        const d = new Date(dd);
        return (
          d.getFullYear() === year &&
          d.getMonth() === month &&
          d.getDate() === day
        );
      });
    const isDisbursedDay = (day) =>
      disbursedDates.some((dd) => {
        const d = new Date(dd);
        return (
          d.getFullYear() === year &&
          d.getMonth() === month &&
          d.getDate() === day
        );
      });
    const monthName = new Date(year, month).toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const borderColor = isActive
      ? "#270086"
      : isClickable
        ? "rgba(64,64,64,0.2)"
        : "rgba(64,64,64,0.1)";
    const bgColor = isActive ? "#f8f6ff" : isCurrentMonth ? "#fffdf5" : "#fff";

    return (
      <div
        onClick={isClickable ? onClick : undefined}
        title={
          isClickable
            ? "Click to view past period"
            : isCurrentMonth
              ? "Current active period"
              : "Future period — not yet active"
        }
        style={{
          flex: 1,
          cursor: isClickable ? "pointer" : "default",
          padding: "10px 6px",
          borderRadius: "6px",
          border: isActive ? `2px solid #270086` : `1px solid ${borderColor}`,
          backgroundColor: bgColor,
          opacity: !isClickable && !isCurrentMonth && !isActive ? 0.5 : 1,
          transition: "all 0.2s ease",
          position: "relative",
        }}
      >
        {!isClickable && !isCurrentMonth && (
          <div
            style={{
              position: "absolute",
              top: "6px",
              right: "6px",
              fontSize: "9px",
              color: "#bbb",
            }}
          >
            🔒
          </div>
        )}

        {isCurrentMonth && (
          <div
            style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              fontSize: "8px",
              backgroundColor: "#F99E52",
              color: "#fff",
              borderRadius: "4px",
              padding: "1px 5px",
              fontWeight: 700,
            }}
          >
            NOW
          </div>
        )}
        <div style={{ textAlign: "center", marginBottom: "6px" }}>
          <div
            style={{
              fontSize: "11px",
              fontWeight: 600,
              color: isActive ? "#270086" : isCurrentMonth ? "#F99E52" : "#555",
            }}
          >
            {label}
          </div>
          <div style={{ fontSize: "10px", color: "#888" }}>{monthName}</div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            textAlign: "center",
          }}
        >
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <div
              key={i}
              style={{
                fontSize: "8px",
                color: "#aaa",
                fontWeight: 600,
                padding: "2px 0",
              }}
            >
              {d}
            </div>
          ))}
          {cells.map((day, i) => {
            if (day === null) return <div key={`e${i}`} />;
            const call = isCallDay(day);
            const td = isToday(day);
            const due = isDueDay(day);
            const dis = isDisbursedDay(day);
            // Priority: disbursed > due > call > today
            let bg = "transparent";
            let fg = "#333";
            if (dis) {
              bg = "#00bd35";
              fg = "#fff";
            } else if (due) {
              bg = "#ca5356";
              fg = "#fff";
            } else if (call) {
              bg = "#270086";
              fg = "#fff";
            } else if (td) {
              bg = "#F99E52";
              fg = "#fff";
            }
            return (
              <div
                key={i}
                style={{
                  fontSize: "9px",
                  width: "18px",
                  height: "18px",
                  margin: "1px auto",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: bg,
                  color: fg,
                  fontWeight: call || due || dis || td ? 700 : 400,
                }}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const calendarMonths = useMemo(() => {
    const fallback = PERIODS.map((_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() + i);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
    if (!leadDetail?.calls?.length) return fallback;
    // Use parseDate to correctly handle DD-MM-YYYY format from API
    const dates = leadDetail.calls
      .map((c) => parseDate(c.call_date))
      .filter((d) => !isNaN(d));
    if (!dates.length) return fallback;
    const ref = new Date(Math.min(...dates));
    return PERIODS.map((_, i) => {
      const d = new Date(ref);
      d.setMonth(d.getMonth() + i);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }, [leadDetail]);

  useEffect(() => {
    if (!calendarMonths.length) return;
    const today = new Date();
    const ty = today.getFullYear();
    const tm = today.getMonth();

    // Select the calendar that matches the current month.
    // If the current month is beyond all calendars, select the last one.
    let activeIdx = 0;
    for (let i = 0; i < calendarMonths.length; i++) {
      const { year, month } = calendarMonths[i];
      // Include current month (<=) so it is selected by default
      if (year < ty || (year === ty && month <= tm)) {
        activeIdx = i;
      }
    }
    setSelectedPeriod(PERIODS[activeIdx].key);
  }, [calendarMonths]);

  const isPastMonth = (year, month) => {
    const today = new Date();
    // Include the current month so the active calendar is also clickable
    return (
      year < today.getFullYear() ||
      (year === today.getFullYear() && month <= today.getMonth())
    );
  };

  const disbursedDate = useMemo(() => {
    if (!calendarMonths.length) return null;
    const { year, month } = calendarMonths[0];
    return new Date(year, month, 2).toISOString().split("T")[0];
  }, [calendarMonths]);

  const dueDate = useMemo(() => {
    if (!calendarMonths.length) return null;
    const { year, month } = calendarMonths[0];
    return new Date(year, month, 17).toISOString().split("T")[0];
  }, [calendarMonths]);

  const allCallDates = useMemo(() => {
    // Normalize to ISO YYYY-MM-DD so MiniCalendar date comparison works correctly
    const apiDates = (leadDetail?.calls || []).map((c) => {
      const d = parseDate(c.call_date);
      return isNaN(d) ? c.call_date : d.toISOString().split("T")[0];
    });
    if (!calendarMonths.length) return apiDates;
    const { year, month } = calendarMonths[0];
    const mockCallDate = new Date(year, month, 24).toISOString().split("T")[0];
    return [...new Set([...apiDates, mockCallDate])];
  }, [leadDetail, calendarMonths]);

  const PeriodLabel = PERIODS.find((p) => p.key === selectedPeriod)?.label;

  return (
    <div className="dashboard-body">
      <Header />
      <NavBar />

      {/* ══════════════════════════════════════════════════
          DELETE NOTE CONFIRMATION MODAL
      ══════════════════════════════════════════════════ */}
      {confirmDeleteNoteId !== null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setConfirmDeleteNoteId(null)}
        >
          <div
            style={{
              background: "#FDFCFF",
              borderRadius: "12px",
              padding: "32px",
              width: "380px",
              maxWidth: "90vw",
              boxShadow: "0 12px 40px rgba(39,16,120,0.18)",
              border: "1px solid #DFDFDF",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <i
              className="fa fa-trash"
              style={{
                fontSize: "32px",
                color: "#ef4444",
                marginBottom: "14px",
                display: "block",
              }}
            />
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#271078",
                marginBottom: "8px",
              }}
            >
              Delete Note?
            </h3>
            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "22px",
              }}
            >
              Are you sure you want to delete this note? This action cannot be
              undone.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "12px",
              }}
            >
              <button
                onClick={() => setConfirmDeleteNoteId(null)}
                style={{
                  backgroundColor: "white",
                  color: "#484848",
                  border: "1px solid #C2C2C2",
                  borderRadius: "8px",
                  padding: "8px 22px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const id = confirmDeleteNoteId;
                  setConfirmDeleteNoteId(null);
                  deleteNote(id);
                }}
                style={{
                  backgroundColor: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 22px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container-fluid">
        <div className="my-call">
          <div className="my-calls-column">
            <div className="calls-top-pannel">
              <div className="row">
                <div className="col-md-6">
                  <div className="pannel-heading clearfix">
                    <div className="pannel-heading-icon">
                      <i className="flaticon-incoming-call"></i>
                    </div>
                    <div className="pannel-heading-info">
                      <p>Call Details</p>
                      <h3>
                        Resolution Tracking{" "}
                        <i className="icon-down-arrow-round"></i>
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="pannel-nav panel-nav-right clearfix">
                    <ul className="clearfix">
                      <li>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            // history.push("/call/promise-to-pay");
                            history.push("/call/call-status");
                          }}
                        >
                          ← Back to List
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {leadDetail && (
          <>
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr) auto",
                  gap: "16px",
                }}
              >
                {[
                  { l: "Customer", v: customerInfo.customer, color: "#60A5FA" },
                  { l: "Product", v: customerInfo.product, color: "#2DD4BF" },
                  { l: "Agent", v: customerInfo.agent, color: "#A78BFA" },
                  { l: "Category", v: customerInfo.category, color: "#F472B6" },
                  {
                    l: "Status",
                    v: leadDetail?.collection_status || "Partial Settlement",
                    color: "#FB923C",
                  },
                  {
                    l: "Amount",
                    v: leadDetail
                      ? `Collected: ${leadDetail.collected_amount_formatted || "₱0.00"} | Balance: ${leadDetail.target_amount_formatted || "₱0.00"}`
                      : "Collected: ₱0.00 | Balance: ₱0.00",
                    color: "#34D399",
                  },
                ].map((item, idx, arr) => (
                  <div
                    key={item.l}
                    style={{
                      background: "#fff",
                      border: "1px solid rgba(64,64,64,0.1)",
                      borderLeft: `5px solid ${item.color}`,
                      borderRadius: "8px",
                      padding: "16px 20px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        marginBottom: "6px",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {item.l}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#1A0059",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="my-call">
              <div
                className="my-calls-column"
                style={{ paddingBottom: "0px", marginBottom: "0px" }}
              >
                <div style={{ display: "flex", gap: "12px", padding: "5px 0" }}>
                  {PERIODS.map((period, idx) => {
                    const { year, month } = calendarMonths[idx];
                    const clickable = isPastMonth(year, month);
                    return (
                      <MiniCalendar
                        key={period.key}
                        year={year}
                        month={month}
                        callDates={allCallDates}
                        dueDates={dueDate ? [dueDate] : []}
                        disbursedDates={disbursedDate ? [disbursedDate] : []}
                        isActive={selectedPeriod === period.key}
                        isClickable={clickable}
                        onClick={() =>
                          clickable && setSelectedPeriod(period.key)
                        }
                        label={period.label}
                      />
                    );
                  })}
                </div>

                {/* ── Period Snapshot (inside the white box, below active calendar) ── */}
                {(() => {
                  const idx = PERIODS.findIndex(
                    (p) => p.key === selectedPeriod,
                  );
                  const trail = PERIOD_TRAIL_DATA[selectedPeriod] || {};
                  const currentCall =
                    currentCalls[0] || leadDetail?.calls?.[0] || {};
                  const nextActionData = currentCall?.next_action || {};

                  const rows = [
                    {
                      label: "Consumable Insight",
                      value:
                        nextActionData.agent_feedback ||
                        trail.consumableInsight,
                      icon: "💡",
                      color: "#270086",
                      bg: "#ede8ff",
                    },
                    {
                      label: "Next Action",
                      value: nextActionData.next_action || trail.nextAction,
                      icon: "▶",
                      color: "#1A0059",
                      bg: "#e0f7ec",
                    },
                    {
                      label: "Missing Data Point",
                      value:
                        nextActionData.meta?.missing_datapoints?.join(", ") ||
                        trail.missingDataPoint,
                      icon: "⚠",
                      color: "#c0392b",
                      bg: "#fde8e8",
                    },
                  ];
                  // Centre of first calendar column (fixed)
                  const arrowLeft = `${((0 + 0.5) / 4) * 100}%`;
                  // Left edge of snapshot card (fixed under first calendar)
                  const cardLeft = `calc(${0 * 25}% + 6px)`;
                  return (
                    <div
                      style={{
                        position: "relative",
                        height: "190px",
                        marginTop: "4px",
                      }}
                    >
                      {/* Downward arrow connector */}
                      <div
                        style={{
                          position: "absolute",
                          left: arrowLeft,
                          top: 0,
                          transform: "translateX(-50%)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",

                          pointerEvents: "none",
                          zIndex: 2,
                        }}
                      >
                        <div
                          style={{
                            width: "2px",
                            height: "18px",
                            backgroundImage:
                              "repeating-linear-gradient(to bottom, #270086 0, #270086 5px, transparent 5px, transparent 9px)",
                          }}
                        />
                        <div
                          style={{
                            width: 0,
                            height: 0,
                            borderLeft: "7px solid transparent",
                            borderRight: "7px solid transparent",
                            borderTop: "8px solid #270086",
                          }}
                        />
                      </div>

                      {/* Snapshot card */}
                      <div
                        style={{
                          position: "absolute",
                          top: "26px",
                          left: cardLeft,
                          width: "calc(25% - 16px)",

                          backgroundColor: "#fff",
                          border: "2px solid #270086",
                          borderRadius: "10px",
                          overflow: "hidden",
                          boxShadow: "0 4px 18px rgba(39,0,134,0.12)",
                          zIndex: 1,
                        }}
                      >
                        {/* 3 rows */}
                        {rows.map((row, i, arr) => (
                          <div
                            key={row.label}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "8px",
                              padding: "9px 12px",
                              borderBottom:
                                i < arr.length - 1
                                  ? "1px solid #ede8fc"
                                  : "none",
                              backgroundColor: i % 2 === 0 ? "#fdfcff" : "#fff",
                            }}
                          >
                            <div
                              style={{
                                flexShrink: 0,
                                width: "22px",
                                height: "22px",
                                borderRadius: "50%",
                                backgroundColor: row.bg,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                              }}
                            >
                              {row.icon}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: row.color,

                                  letterSpacing: "0.05em",
                                  marginBottom: "2px",
                                }}
                              >
                                {row.label} :{" "}
                                <span
                                  style={{
                                    fontSize: "13px",
                                    color: "#333",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {row.value}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Legend — centered ── */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "20px",
                    padding: "10px 4px 6px",
                    flexWrap: "wrap",
                    marginTop: "35px",
                  }}
                >
                  {[
                    { color: "#00bd35", label: "Disbursed Date" },
                    { color: "#ca5356", label: "Due Date" },
                    { color: "#270086", label: "Call Date" },
                    { color: "#F99E52", label: "Today" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: "11px", color: "#666" }}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>

                {resolution && !resolution.ongoing && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "10px 0 5px",
                    }}
                  >
                    <span style={styles.badge(resolution.color)}>
                      {resolution.icon} Activity Closed — {resolution.label}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div
              className="lead-tab"
              style={{ display: "flex", alignItems: "stretch", gap: "10px" }}
            >
              <div
                style={{
                  flex: "5.5",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <div
                  className="my-call"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    className="my-calls-column p-0"
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      className="tab_area"
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <ul className="tabs01 clearfix">
                        {[
                          "calls",
                          "email",
                          "notes",
                          "reminders",
                          "recommendations",
                        ].map((tab) => (
                          <li
                            key={tab}
                            className={activetab === tab ? "active" : ""}
                          >
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setActivetab(tab);
                              }}
                              style={{ textTransform: "capitalize" }}
                            >
                              {tab}
                            </a>
                          </li>
                        ))}
                      </ul>

                      <div className="tab_container" style={{ flex: 1 }}>
                        <div
                          className="tab_content"
                          style={{ minHeight: "200px" }}
                        >
                          {activetab === "calls" && (
                            <div
                              style={{
                                maxHeight: "280px",
                                overflowY: "auto",
                              }}
                            >
                              {leadDetail?.calls?.length > 0 ? (
                                <table
                                  style={{
                                    width: "100%",
                                    fontSize: "13px",
                                    borderCollapse: "collapse",
                                  }}
                                >
                                  <thead
                                    style={{
                                      backgroundColor: "#f9fafb",
                                      color: "#374151",
                                    }}
                                  >
                                    <tr>
                                      {[
                                        "Call Id",
                                        "Date",
                                        "Customer",
                                        "Category",
                                        "Product",
                                        "Agent",
                                      ].map((h) => (
                                        <th key={h} style={styles.thTd}>
                                          {h}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(leadDetail?.calls || []).map((call) => (
                                      <tr
                                        key={call.call_id}
                                        style={{
                                          borderBottom: "1px solid #eef2f5",
                                        }}
                                      >
                                        <td
                                          style={{
                                            ...styles.thTd,
                                            fontWeight: 500,
                                            color: "#1A0059",
                                          }}
                                        >
                                          {call?.call_id?.slice(0, 6)}
                                        </td>
                                        <td style={styles.thTd}>
                                          {call?.call_date}
                                        </td>
                                        <td style={styles.thTd}>
                                          {call?.customer}
                                        </td>
                                        <td style={styles.thTd}>
                                          {leadDetail?.category || "N/A"}
                                        </td>
                                        <td style={styles.thTd}>
                                          {call?.product}
                                        </td>
                                        <td style={styles.thTd}>
                                          {call?.agent}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <div style={styles.emptyState}>
                                  No calls recorded for this period
                                </div>
                              )}
                            </div>
                          )}

                          {/* Email */}
                          {activetab === "email" && (
                            <>
                              <div
                                style={{
                                  backgroundColor: "#eef2f5",
                                  padding: "20px",
                                  maxHeight: "220px",
                                  overflowY: "auto",
                                  borderRadius: "6px",
                                }}
                              >
                                <textarea
                                  className="w-full p-4"
                                  style={{
                                    width: "100%",
                                    height: "120px",
                                    fontSize: "14px",
                                    lineHeight: "1.6",
                                    borderRadius: "6px",
                                    border: "1px solid transparent",
                                    backgroundColor: "transparent",
                                    resize: "none",
                                    outline: "none",
                                    cursor: "text",
                                    padding: "4px",
                                    color: "#333",
                                    boxSizing: "border-box",
                                    transition: "border-color 0.2s ease, background-color 0.2s ease",
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.border = "1px solid #270086";
                                    e.target.style.backgroundColor = "#fff";
                                    e.target.style.borderRadius = "6px";
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.border = "1px solid transparent";
                                    e.target.style.backgroundColor = "transparent";
                                  }}
                                  defaultValue="I hope this message finds you well! I wanted to touch base regarding the payment commitment. If you have any updates or need assistance, please let me know. Also, if you could confirm the agreed payment date, that would be great."
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  padding: "12px 0",
                                }}
                              >
                                <button style={styles.sendBtn}>Send</button>
                              </div>
                            </>
                          )}

                          {activetab === "notes" && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                padding: "15px 5px",
                              }}
                            >
                              {/* Add note input row */}
                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  marginBottom: "8px",
                                }}
                              >
                                <input
                                  type="text"
                                  placeholder="Add Note..."
                                  value={newtask}
                                  onChange={(e) => setNewtask(e.target.value)}
                                  onKeyDown={(e) =>
                                    e.key === "Enter" && addNotes()
                                  }
                                  style={{
                                    flex: 1,
                                    padding: "8px 12px",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    outline: "none",
                                  }}
                                />
                                <button
                                  onClick={addNotes}
                                  disabled={noteLoading || !newtask.trim()}
                                  style={{
                                    ...styles.sendBtn,
                                    opacity:
                                      noteLoading || !newtask.trim() ? 0.6 : 1,
                                    cursor:
                                      noteLoading || !newtask.trim()
                                        ? "not-allowed"
                                        : "pointer",
                                    minWidth: "64px",
                                  }}
                                >
                                  {noteLoading ? "…" : "Add"}
                                </button>
                              </div>

                              {/* Inline error */}
                              {noteError && (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#ca5356",
                                    marginBottom: "8px",
                                    paddingLeft: "4px",
                                  }}
                                >
                                  ⚠ {noteError}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Reminders */}
                          {activetab === "reminders" && (
                            <div style={{ padding: "15px 5px" }}>
                              {[
                                {
                                  date: "15 Apr 2025",
                                  status: "Done",
                                  text: "Follow up with customer regarding payment confirmation for April instalment.",
                                },
                                {
                                  date: "22 Apr 2025",
                                  status: "Pending",
                                  text: "Send formal reminder email with updated payment schedule and due date.",
                                },
                                {
                                  date: "30 Apr 2025",
                                  status: "Pending",
                                  text: "Escalate to senior agent if no response received by end of month.",
                                },
                                {
                                  date: "05 May 2025",
                                  status: "Pending",
                                  text: "Review account status and update resolution category based on customer response.",
                                },
                              ].map((reminder, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "12px",
                                    padding: "10px 0",
                                    borderBottom:
                                      idx < 3 ? "1px solid #f0f0f0" : "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      minWidth: "80px",
                                      fontSize: "11px",
                                      color: "#888",
                                      paddingTop: "2px",
                                    }}
                                  >
                                    {reminder.date}
                                  </div>
                                  <div
                                    style={{
                                      flex: 1,
                                      fontSize: "13px",
                                      color: "#444",
                                      lineHeight: "1.5",
                                    }}
                                  >
                                    {reminder.text}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      padding: "2px 10px",
                                      borderRadius: "12px",
                                      backgroundColor:
                                        reminder.status === "Done"
                                          ? "#e6f9ed"
                                          : "#fff7e6",
                                      color:
                                        reminder.status === "Done"
                                          ? "#00bd35"
                                          : "#c47a1e",
                                      border: `1px solid ${reminder.status === "Done" ? "#00bd3530" : "#F99E5230"}`,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {reminder.status}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Recommendations */}
                          {activetab === "recommendations" && (
                            <div style={{ padding: "15px 5px" }}>
                              <div style={styles.recBox}>
                                <div style={styles.recLabel}>
                                  Dataklout Recommendation — {PeriodLabel}
                                </div>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    lineHeight: "1.6",
                                    color: "#333",
                                    margin: 0,
                                  }}
                                >
                                  {currentRec}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  flex: "4.5",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                    }}
                  >
                    <div
                      className="my-call"
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        className="my-calls-column p-0"
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          className="tab_area"
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <ul className="tabs01 clearfix">
                            <div className="white-box-header">
                              <h3>
                                {activetab.charAt(0).toUpperCase() +
                                  activetab.slice(1)}
                              </h3>
                            </div>
                          </ul>
                          <div className="tab_container" style={{ flex: 1 }}>
                            <div
                              className="tab_content"
                              style={{
                                minHeight: "200px",
                                maxHeight: "320px",
                                overflowY: "auto",
                              }}
                            >
                              {(() => {
                                const data = {
                                  calls: [
                                    {
                                      date: "04 Jun 2025",
                                      text: "Payment schedule discussed",
                                    },
                                    {
                                      date: "28 May 2025",
                                      text: "Extension requested",
                                    },
                                    {
                                      date: "20 May 2025",
                                      text: "No answer — voicemail left",
                                    },
                                    {
                                      date: "12 May 2025",
                                      text: "Initial outreach",
                                    },
                                    {
                                      date: "05 May 2025",
                                      text: "Customer busy",
                                    },
                                  ],
                                  email: [
                                    {
                                      from: "christy@dataklout.com",
                                      to: "chanteuse@email.com",
                                      subject:
                                        "Payment Reminder — Personal Loan Account",
                                      date: "03 Jun 2025, 04:12 PM",
                                      body: "Dear Chanteuse,\n\nThis is a friendly reminder regarding your personal loan account. As per our records, a balance of ₱7,000.00 remains outstanding.\n\nKindly arrange the payment at your earliest convenience to avoid any additional charges. If you have already made the payment, please disregard this email.\n\nThank you for your cooperation.\n\nBest regards,\nChristy\nCollections Team",
                                    },
                                    {
                                      from: "christy@dataklout.com",
                                      to: "chanteuse@email.com",
                                      subject:
                                        "Revised Payment Plan — Action Required",
                                      date: "25 May 2025, 10:30 AM",
                                      body: "Dear Chanteuse,\n\nFollowing our recent conversation, please find below the revised payment plan for your outstanding balance:\n\n• Instalment 1: ₱3,500.00 — Due 01 Jun 2025\n• Instalment 2: ₱3,500.00 — Due 15 Jun 2025\n\nPlease confirm your acceptance by replying to this email. If you have any questions, feel free to reach out.\n\nRegards,\nChristy\nCollections Team",
                                    },
                                    {
                                      from: "noreply@dataklout.com",
                                      to: "chanteuse@email.com",
                                      subject: "Overdue Payment Notice",
                                      date: "15 May 2025, 09:00 AM",
                                      body: "Dear Chanteuse,\n\nOur records indicate that your personal loan payment is now overdue. The outstanding amount is ₱12,000.00.\n\nPlease make the payment immediately to avoid further action on your account. You can pay via our online portal or visit the nearest branch.\n\nThank you,\nDataklout Collections",
                                    },
                                  ],
                                  notes: (leadDetail?.notes || []).map(
                                    (n) => ({
                                      date: n.created_at || "",
                                      text: n.text || "",
                                      id: n.id,
                                    })
                                  ),
                                  reminders: [
                                    {
                                      date: "10 Jun 2025",
                                      text: "Follow up on payment commitment",
                                    },
                                    {
                                      date: "05 Jun 2025",
                                      text: "Review account status",
                                    },
                                    {
                                      date: "30 May 2025",
                                      text: "Send formal reminder",
                                    },
                                    {
                                      date: "22 May 2025",
                                      text: "Check customer response",
                                    },
                                  ],
                                  recommendations: [
                                    {
                                      date: "04 Jun 2025",
                                      text: "Offer flexible 3-instalment plan",
                                    },
                                    {
                                      date: "28 May 2025",
                                      text: "Switch to phone channel",
                                    },
                                    {
                                      date: "15 May 2025",
                                      text: "Escalate to senior agent",
                                    },
                                  ],
                                };

                                const items = data[activetab] || [];

                                return items.length > 0 ? (
                                  activetab === "email" ? (
                                    <div
                                      style={{
                                        padding: "8px 0",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "12px",
                                      }}
                                    >
                                      {items.map((item, idx) => (
                                        <div
                                          key={idx}
                                          style={{
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "8px",
                                            overflow: "hidden",
                                            backgroundColor: "#fff",
                                          }}
                                        >
                                          <div
                                            style={{
                                              padding: "8px 14px",
                                              backgroundColor: "#f9fafb",
                                              borderBottom: "1px solid #e2e8f0",
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: "#1A0059",
                                              }}
                                            >
                                              {item.subject}
                                            </span>
                                            <span
                                              style={{
                                                fontSize: "11px",
                                                color: "#999",
                                              }}
                                            >
                                              {item.date}
                                            </span>
                                          </div>
                                          <div
                                            style={{
                                              padding: "12px 14px",
                                              fontSize: "13px",
                                              color: "#333",
                                              lineHeight: "1.6",
                                              whiteSpace: "pre-line",
                                            }}
                                          >
                                            {item.body}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div style={{ padding: "4px 0" }}>
                                      {items.map((item, idx) => (
                                        <div
                                          key={idx}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px",
                                            padding: "10px 8px",
                                            borderBottom:
                                              idx < items.length - 1
                                                ? "1px solid #f0f0f0"
                                                : "none",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "11px",
                                              color: "#999",
                                              minWidth: "85px",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {item.date}
                                          </span>
                                          <span
                                            style={{
                                              flex: 1,
                                              fontSize: "13px",
                                              color: "#333",
                                            }}
                                          >
                                            {item.text}
                                          </span>
                                          {activetab === "notes" && item.id && (
                                            <button
                                              onClick={() =>
                                                setConfirmDeleteNoteId(item.id)
                                              }
                                              title="Delete note"
                                              style={{
                                                background: "none",
                                                border: "none",
                                                cursor: "pointer",
                                                color: "#c2c2c2",
                                                padding: "4px",
                                                flexShrink: 0,
                                                transition: "color 0.2s",
                                              }}
                                              onMouseEnter={(e) =>
                                                (e.currentTarget.style.color =
                                                  "#ef4444")
                                              }
                                              onMouseLeave={(e) =>
                                                (e.currentTarget.style.color =
                                                  "#c2c2c2")
                                              }
                                            >
                                              <i
                                                className="fa fa-trash"
                                                aria-hidden="true"
                                                style={{ fontSize: "14px" }}
                                              />
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )
                                ) : (
                                  <div
                                    style={{
                                      padding: "40px 20px",
                                      textAlign: "center",
                                      color: "#bbb",
                                      fontSize: "13px",
                                    }}
                                  >
                                    No records available
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="my-call"
              // style={{ marginTop: "15px", marginBottom: "0px" }}
              style={{ margin: "0px" }}
            >
              <div className="my-calls-column" style={{ padding: "20px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                  }}
                >
                  {/* Next Action Box */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid rgba(64,64,64,0.15)",
                      borderRadius: "8px",
                      padding: "16px 20px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#1A0059",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        marginBottom: "12px",
                        borderBottom: "1px solid rgba(64,64,64,0.1)",
                        paddingBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span style={{ fontSize: "14px" }}>▶</span>
                      Next Action
                    </div>
                    <div>
                      <p
                        style={{
                          fontSize: "13.5px",
                          color: "#444",
                          lineHeight: "1.6",
                          margin: 0,
                        }}
                      >
                        <strong>Follow-up required:</strong> The customer has
                        requested a revised payment plan. Contact the customer
                        by <strong>Friday, 4:00 PM</strong> with the updated
                        restructuring details and confirm their agreement.
                      </p>
                    </div>
                  </div>

                  {/* Resolution Box */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid rgba(64,64,64,0.15)",
                      borderRadius: "8px",
                      padding: "16px 20px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#270086",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        marginBottom: "12px",
                        borderBottom: "1px solid rgba(64,64,64,0.1)",
                        paddingBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {/* <span style={{ fontSize: "14px" }}>✅</span> */}
                      Resolution
                    </div>
                    <div>
                      <p
                        style={{
                          fontSize: "13.5px",
                          color: "#444",
                          lineHeight: "1.6",
                          margin: 0,
                        }}
                      >
                        <strong>In Progress:</strong> P2P resolution is
                        currently active. The account is under special
                        monitoring. If the next payment is not received within
                        the 14-day grace period, escalate the case.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- Intent Section --- */}
            <div className="my-call" style={{ marginTop: "15px" }}>
              <div className="my-calls-column" style={{ padding: "20px" }}>
                <div style={{ marginBottom: "20px" }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#1A0059",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "1px solid rgba(64,64,64,0.1)",
                      paddingBottom: "10px",
                      margin: 0,
                    }}
                  >
                    Intent Analysis
                  </h3>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1fr)",
                    gap: "24px",
                  }}
                >
                  {/* Customer Column */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid rgba(64,64,64,0.15)",
                      borderRadius: "8px",
                      padding: "16px 20px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#1A0059",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        marginBottom: "16px",
                        borderBottom: "1px solid rgba(64,64,64,0.1)",
                        paddingBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>Customer</span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "13px",
                          border: "1px solid #ccc",
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: "#f9fafb" }}>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: "8px",
                                textAlign: "center",
                                color: "#374151",
                              }}
                            >
                              Classification
                            </th>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: "8px",
                                textAlign: "center",
                                color: "#374151",
                              }}
                            >
                              Attributes
                            </th>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: "8px",
                                textAlign: "center",
                                color: "#374151",
                              }}
                            >
                              Score (/100) | Value
                            </th>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: "8px",
                                textAlign: "center",
                                color: "#374151",
                              }}
                            >
                              Avg Score
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* tonal section */}
                          {(() => {
                            const attrs =
                              callInsight?.collection_intent?.customer
                                ?.classifications?.tonal?.attributes ?? [];
                            const totalScore =
                              attrs.length > 0
                                ? Math.round(
                                    (attrs.reduce(
                                      (sum, item) => sum + (item.score || 0),
                                      0,
                                    ) /
                                      (attrs.length * 100)) *
                                    100
                                  ) + "/100"
                                : "-";
                            return attrs.map((item, idx) => (
                              <tr key={`c-tonal-${idx}`}>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Tonal
                                  </td>
                                )}
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.attribute}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.score}
                                </td>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {totalScore}
                                  </td>
                                )}
                              </tr>
                            ));
                          })()}
                          {/* textual section */}
                          {(() => {
                            const attrs =
                              callInsight?.collection_intent?.customer
                                ?.classifications?.textual?.attributes ?? [];
                            const totalScore =
                              attrs.length > 0
                                ? Math.round(
                                    (attrs.reduce(
                                      (sum, item) => sum + (item.score || 0),
                                      0,
                                    ) /
                                      (attrs.length * 100)) *
                                    100
                                  ) + "/100"
                                : "-";
                            return attrs.map((item, idx) => (
                              <tr key={`c-textual-${idx}`}>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Textual
                                  </td>
                                )}
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.attribute}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.score}
                                </td>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {totalScore}
                                  </td>
                                )}
                              </tr>
                            ));
                          })()}
                          {/* mathematical section */}
                          {(() => {
                            const attrs =
                              callInsight?.collection_intent?.customer
                                ?.classifications?.mathematical?.attributes ??
                              [];
                            if (attrs.length === 0) return null;
                            const totalScore =
                              callInsight?.collection_intent?.customer
                                ?.classifications?.mathematical?.total_score ??
                              "-";
                            return attrs.map((item, idx) => (
                              <tr key={`c-math-${idx}`}>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Mathematical
                                  </td>
                                )}
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.attribute}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.score}
                                </td>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {totalScore}
                                  </td>
                                )}
                              </tr>
                            ));
                          })()}
                          {/* data points section */}
                          {(() => {
                            const staticData =
                              callInsight?.static_datapoints?.customer || {};
                            const attrs = Object.entries(staticData).filter(
                              ([key]) => key !== "intent_score",
                            );
                            if (attrs.length === 0) return null;
                            const totalScore =
                              staticData.intent_score != null
                                ? staticData.intent_score + "/100"
                                : "-";
                            return attrs.map(([key, value], idx) => (
                              <tr key={`c-data-${idx}`}>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Data Points
                                  </td>
                                )}
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {key.replace(/_/g, " ")}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {value === "NOT MENTIONED" ? (
                                    <div
                                      style={{
                                        backgroundColor: "red",
                                        color: "white",
                                        borderRadius: "50%",
                                        padding: "4px",
                                        fontSize: "10px",
                                        width: "18px",
                                        height: "18px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        margin: "0 auto",
                                      }}
                                    >
                                      ✕
                                    </div>
                                  ) : (
                                    value
                                  )}
                                </td>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {totalScore}
                                  </td>
                                )}
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                      <div
                        style={{
                          marginTop: "10px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "red",
                            color: "white",
                            borderRadius: "50%",
                            padding: "4px",
                            fontSize: "10px",
                            width: "18px",
                            height: "18px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          ✕
                        </div>
                        <span>Not Mentioned</span>
                      </div>
                    </div>
                  </div>

                  {/* Agent Column */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid rgba(64,64,64,0.15)",
                      borderRadius: "8px",
                      padding: "16px 20px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#1A0059",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        marginBottom: "16px",
                        borderBottom: "1px solid rgba(64,64,64,0.1)",
                        paddingBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>Agent</span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "13px",
                          border: "1px solid #ccc",
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: "#f9fafb" }}>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: "8px",
                                textAlign: "center",
                                color: "#374151",
                              }}
                            >
                              Classification
                            </th>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: "8px",
                                textAlign: "center",
                                color: "#374151",
                              }}
                            >
                              Attributes
                            </th>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: "8px",
                                textAlign: "center",
                                color: "#374151",
                              }}
                            >
                              Score (/100) | Value
                            </th>
                            <th
                              style={{
                                border: "1px solid #ccc",
                                padding: "8px",
                                textAlign: "center",
                                color: "#374151",
                              }}
                            >
                              Avg Score
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* tonal section */}
                          {(() => {
                            const attrs =
                              callInsight?.collection_intent?.agent
                                ?.classifications?.tonal?.attributes ?? [];
                            const totalScore =
                              attrs.length > 0
                                ? Math.round(
                                    (attrs.reduce(
                                      (sum, item) => sum + (item.score || 0),
                                      0,
                                    ) /
                                      (attrs.length * 100)) *
                                    100
                                  ) + "/100"
                                : "-";
                            return attrs.map((item, idx) => (
                              <tr key={`a-tonal-${idx}`}>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Tonal
                                  </td>
                                )}
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.attribute}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.score}
                                </td>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {totalScore}
                                  </td>
                                )}
                              </tr>
                            ));
                          })()}
                          {/* textual section */}
                          {(() => {
                            const attrs =
                              callInsight?.collection_intent?.agent
                                ?.classifications?.textual?.attributes ?? [];
                            const totalScore =
                              attrs.length > 0
                                ? Math.round(
                                    (attrs.reduce(
                                      (sum, item) => sum + (item.score || 0),
                                      0,
                                    ) /
                                      (attrs.length * 100)) *
                                    100
                                  ) + "/100"
                                : "-";
                            return attrs.map((item, idx) => (
                              <tr key={`a-textual-${idx}`}>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Textual
                                  </td>
                                )}
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.attribute}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.score}
                                </td>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {totalScore}
                                  </td>
                                )}
                              </tr>
                            ));
                          })()}
                          {/* mathematical section */}
                          {(() => {
                            const attrs =
                              callInsight?.collection_intent?.agent
                                ?.classifications?.mathematical?.attributes ??
                              [];
                            if (attrs.length === 0) return null;
                            const totalScore =
                              callInsight?.collection_intent?.agent
                                ?.classifications?.mathematical?.total_score ??
                              "-";
                            return attrs.map((item, idx) => (
                              <tr key={`a-math-${idx}`}>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Mathematical
                                  </td>
                                )}
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.attribute}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.score}
                                </td>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {totalScore}
                                  </td>
                                )}
                              </tr>
                            ));
                          })()}
                          {/* data points section */}
                          {(() => {
                            const staticData =
                              callInsight?.static_datapoints?.agent || {};
                            const attrs = Object.entries(staticData).filter(
                              ([key]) => key !== "intent_score",
                            );
                            if (attrs.length === 0) return null;
                            const totalScore =
                              staticData.intent_score != null
                                ? staticData.intent_score + "/100"
                                : "-";
                            return attrs.map(([key, value], idx) => (
                              <tr key={`a-data-${idx}`}>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Data Points
                                  </td>
                                )}
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {key.replace(/_/g, " ")}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {value === "NOT MENTIONED" ? (
                                    <div
                                      style={{
                                        backgroundColor: "red",
                                        color: "white",
                                        borderRadius: "50%",
                                        padding: "4px",
                                        fontSize: "10px",
                                        width: "18px",
                                        height: "18px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        margin: "0 auto",
                                      }}
                                    >
                                      ✕
                                    </div>
                                  ) : (
                                    value
                                  )}
                                </td>
                                {idx === 0 && (
                                  <td
                                    rowSpan={attrs.length}
                                    style={{
                                      verticalAlign: "middle",
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {totalScore}
                                  </td>
                                )}
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                      <div
                        style={{
                          marginTop: "10px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "red",
                            color: "white",
                            borderRadius: "50%",
                            padding: "4px",
                            fontSize: "10px",
                            width: "18px",
                            height: "18px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          ✕
                        </div>
                        <span>Not Mentioned</span>
                      </div>
                    </div>
                  </div>

                  {/* Stacked Feedback & Prompts Column */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "24px",
                    }}
                  >
                    {/* Combined Feedback Box */}
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid rgba(64,64,64,0.15)",
                        borderRadius: "8px",
                        padding: "16px 20px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#1A0059",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: "12px",
                          borderBottom: "1px solid rgba(64,64,64,0.1)",
                          paddingBottom: "10px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Feedback
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13.5px",
                            color: "#444",
                            lineHeight: "1.6",
                          }}
                        >
                          {(() => {
                            const currentCall = leadDetail?.calls?.[0] || {};
                            const nextActionData =
                              currentCall?.next_action || {};
                            return (
                              nextActionData.agent_feedback ||
                              "No feedback available."
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Prompts Box */}
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid rgba(64,64,64,0.15)",
                        borderRadius: "8px",
                        padding: "16px 20px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                        flex: 1,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#1A0059",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: "16px",
                          borderBottom: "1px solid rgba(64,64,64,0.1)",
                          paddingBottom: "10px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Prompts
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {(() => {
                          const currentCall = leadDetail?.calls?.[0] || {};
                          const nextActionData = currentCall?.next_action || {};
                          const promptText =
                            nextActionData.next_action_english || "";
                          if (!promptText) {
                            return (
                              <div
                                style={{ fontSize: "13.5px", color: "#444" }}
                              >
                                No prompts available.
                              </div>
                            );
                          }
                          return (
                            <div
                              style={{
                                fontSize: "13.5px",
                                color: "#444",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "8px",
                                lineHeight: "1.5",
                              }}
                            >
                              <span
                                style={{ color: "#270086", marginTop: "2px" }}
                              >
                                ▪
                              </span>
                              <span>No Prompts available.</span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {isPending && (
          <div className="empty-call">
            <ClipLoader color="#2056FF" size="50px" />
          </div>
        )}

        {error && (
          <div className="empty-call">
            <p style={{ fontSize: "25px", color: "#FF8520" }}>
              {error === "Connection Error" && <RiSignalWifiErrorFill />}
              {error !== "Connection Error" && <BiError />}
              {error}
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PromiseDetails;
